import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { adminApi, gymApi } from '@/lib/api';
import { Reward, RewardType } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { reportError, setPageArea } from '@/lib/sentry';
import { NoGymSelected } from '@/components/NoGymSelected';
import { RewardParticipantsSheet } from '@/components/RewardParticipantsSheet';
import { RewardAccessCodeCard } from '@/components/RewardAccessCodeCard';
import { Plus, Loader2, Pencil, Trash2, Gift, RotateCw, Flame, Trash, Dices, Trophy, CheckCircle2, Medal, KeyRound } from 'lucide-react';

interface PrizeForm {
    label: string;
    weight: number;
    stock: number | null;
}

interface RewardForm {
    type: RewardType;
    title: string;
    description: string;
    is_active: boolean;
    starts_at: string;
    ends_at: string;
    plays_per_client: number;
    window_days: number;
    threshold: number;
}

const emptyForm: RewardForm = {
    type: 'wheel',
    title: '',
    description: '',
    is_active: true,
    starts_at: '',
    ends_at: '',
    plays_per_client: 1,
    window_days: 30,
    threshold: 10,
};

const toDateInput = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
};

export default function RewardsPage() {
    const { gym, user, refreshAuth } = useAuth();
    const [rewards, setRewards] = useState<Reward[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingReward, setEditingReward] = useState<Reward | null>(null);
    const [form, setForm] = useState<RewardForm>(emptyForm);
    const [prizes, setPrizes] = useState<PrizeForm[]>([{ label: '', weight: 1, stock: null }]);
    const [streakPrize, setStreakPrize] = useState('');
    const [streakStock, setStreakStock] = useState<number | null>(null);
    const [participantsReward, setParticipantsReward] = useState<Reward | null>(null);
    const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
    const [isSavingCodeSetting, setIsSavingCodeSetting] = useState(false);
    const { toast } = useToast();

    const codeRequired = gym?.settings?.rewards_code_required !== false;

    useEffect(() => {
        setPageArea('crm');
    }, []);

    const fetchRewards = async () => {
        if (!gym?.gym_id) return;
        setIsLoading(true);
        try {
            const res = await gymApi.listRewards(gym.gym_id);
            if (res.success) setRewards(res.rewards);
        } catch (error) {
            reportError(error, { operation: 'rewards_list', area: 'crm', extra: { gym_id: gym.gym_id } });
            toast({ title: 'Error', description: 'No se pudieron cargar las recompensas.', variant: 'destructive' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRewards();
    }, [gym?.gym_id]);

    const resetForm = () => {
        setForm(emptyForm);
        setPrizes([{ label: '', weight: 1, stock: null }]);
        setStreakPrize('');
        setStreakStock(null);
        setEditingReward(null);
    };

    const openCreate = () => {
        resetForm();
        setIsDialogOpen(true);
    };

    const openEdit = (reward: Reward) => {
        setEditingReward(reward);
        setForm({
            type: reward.type,
            title: reward.title,
            description: reward.description || '',
            is_active: reward.is_active,
            starts_at: toDateInput(reward.starts_at),
            ends_at: toDateInput(reward.ends_at),
            plays_per_client: reward.plays_per_client ?? 1,
            window_days: reward.config?.window_days ?? 30,
            threshold: reward.config?.threshold ?? 10,
        });
        const rewardPrizes = reward.prizes || [];
        if (reward.type === 'streak') {
            setStreakPrize(rewardPrizes[0]?.label || '');
            setStreakStock(rewardPrizes[0]?.stock ?? null);
            setPrizes([{ label: '', weight: 1, stock: null }]);
        } else {
            setPrizes(
                rewardPrizes.length > 0
                    ? rewardPrizes.map(p => ({ label: p.label, weight: p.weight, stock: p.stock ?? null }))
                    : [{ label: '', weight: 1, stock: null }]
            );
        }
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        if (!gym || !form.title.trim()) {
            toast({ title: 'Datos inválidos', description: 'El título es obligatorio.', variant: 'destructive' });
            return;
        }

        const cleanPrizes = prizes.filter(p => p.label.trim());
        if (form.type === 'wheel' && cleanPrizes.length === 0) {
            toast({ title: 'Datos inválidos', description: 'Agrega al menos un premio a la ruleta.', variant: 'destructive' });
            return;
        }
        if (form.type === 'streak' && !streakPrize.trim()) {
            toast({ title: 'Datos inválidos', description: 'Indica el premio de la racha.', variant: 'destructive' });
            return;
        }

        setIsSubmitting(true);
        try {
            const payload: Partial<Reward> = {
                gym_id: gym.gym_id,
                type: form.type,
                title: form.title.trim(),
                description: form.description.trim() || null,
                is_active: form.is_active,
                starts_at: form.starts_at ? new Date(`${form.starts_at}T00:00:00`).toISOString() : null,
                ends_at: form.ends_at ? new Date(`${form.ends_at}T23:59:59.999`).toISOString() : null,
                plays_per_client: form.type === 'wheel' ? Number(form.plays_per_client) || 1 : 1,
                config: form.type === 'streak' ? { window_days: Number(form.window_days) || 30, threshold: Number(form.threshold) || 10 } : {},
                created_by: editingReward?.created_by || user?.user_id || null,
            };

            const prizePayload = form.type === 'streak'
                ? [{ label: streakPrize.trim(), weight: 1, stock: streakStock ?? null }]
                : cleanPrizes;

            if (editingReward) {
                const res = await gymApi.updateReward(editingReward.id, payload, prizePayload);
                if (res.success) toast({ title: 'Recompensa actualizada' });
            } else {
                const res = await gymApi.createReward(payload, prizePayload);
                if (res.success) toast({ title: 'Recompensa creada' });
            }
            setIsDialogOpen(false);
            resetForm();
            fetchRewards();
        } catch (error: any) {
            reportError(error, { operation: 'rewards_save', area: 'crm', extra: { type: form.type } });
            toast({ title: 'Error', description: error.message || 'No se pudo guardar la recompensa.', variant: 'destructive' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleActive = async (reward: Reward) => {
        try {
            const res = await gymApi.setRewardActive(reward.id, !reward.is_active);
            if (res.success) {
                setRewards(rewards.map(r => (r.id === reward.id ? { ...r, is_active: !reward.is_active } : r)));
                toast({ title: reward.is_active ? 'Recompensa desactivada' : 'Recompensa activada' });
            }
        } catch (error) {
            reportError(error, { operation: 'rewards_toggle', area: 'crm' });
            toast({ title: 'Error', description: 'No se pudo cambiar el estado.', variant: 'destructive' });
        }
    };

    const handleToggleCodeRequired = async (checked: boolean) => {
        if (!gym?.gym_id || isSavingCodeSetting) return;
        setIsSavingCodeSetting(true);
        try {
            await adminApi.updateGym({
                gym_id: gym.gym_id,
                settings: {
                    ...(gym.settings || {}),
                    rewards_code_required: checked,
                },
            });
            await refreshAuth();
            toast({
                title: checked ? 'Código obligatorio' : 'Código opcional',
                description: checked
                    ? 'Los socios deberán ingresar el código diario para ver sus recompensas.'
                    : 'Los socios accederán a sus recompensas sin ingresar código.',
            });
        } catch (error: any) {
            reportError(error, { operation: 'rewards_code_setting', area: 'crm', extra: { gym_id: gym.gym_id } });
            toast({ title: 'Error', description: error?.message || 'No se pudo guardar la configuración.', variant: 'destructive' });
        } finally {
            setIsSavingCodeSetting(false);
        }
    };

    const handleDelete = async (reward: Reward) => {
        if (!window.confirm(`¿Eliminar la recompensa "${reward.title}"?`)) return;
        try {
            const res = await gymApi.deleteReward(reward.id);
            if (res.success) {
                setRewards(rewards.filter(r => r.id !== reward.id));
                toast({ title: 'Recompensa eliminada', variant: 'destructive' });
            }
        } catch (error) {
            reportError(error, { operation: 'rewards_delete', area: 'crm' });
            toast({ title: 'Error', description: 'No se pudo eliminar la recompensa.', variant: 'destructive' });
        }
    };

    const openParticipants = (reward: Reward) => {
        setParticipantsReward(reward);
        setIsParticipantsOpen(true);
    };

    const handleDraw = async (reward: Reward) => {
        if (!window.confirm(`¿Sortear un ganador para "${reward.title}"?`)) return;
        try {
            const res = await gymApi.drawRewardWinner(reward.id);
            if (res?.success) {
                const entriesRes = await gymApi.listRaffleEntries(reward.id);
                const winner = entriesRes.entries.find(e => e.client_id === res.winner_client_id);
                toast({ title: 'Ganador sorteado', description: winner?.client_name || 'Se eligió un ganador.' });
            } else {
                toast({ title: 'No se pudo sortear', description: res?.message || 'Intenta de nuevo.', variant: 'destructive' });
            }
            fetchRewards();
        } catch (error) {
            reportError(error, { operation: 'rewards_draw', area: 'crm' });
            toast({ title: 'Error', description: 'No se pudo sortear el ganador.', variant: 'destructive' });
        }
    };

    const handleClaim = async (reward: Reward) => {
        try {
            const res = await gymApi.markRaffleClaimed(reward.id);
            if (res?.success) {
                toast({ title: 'Premio marcado como reclamado' });
                fetchRewards();
            } else {
                toast({ title: 'No se pudo marcar', description: res?.message, variant: 'destructive' });
            }
        } catch (error) {
            reportError(error, { operation: 'rewards_claim', area: 'crm' });
            toast({ title: 'Error', description: 'No se pudo marcar el reclamo.', variant: 'destructive' });
        }
    };

    const updatePrize = (index: number, patch: Partial<PrizeForm>) => {
        setPrizes(prizes.map((p, i) => (i === index ? { ...p, ...patch } : p)));
    };

    if (!gym) return <DashboardLayout title="Recompensas"><NoGymSelected /></DashboardLayout>;

    return (
        <DashboardLayout
            title="Recompensas"
            subtitle={`Ofertas y dinámicas para los socios de ${gym.name}`}
            actions={
                <Button variant="gradient" onClick={openCreate}>
                    <Plus className="h-4 w-4 mr-2" />
                    Nueva Recompensa
                </Button>
            }
        >
            <Card>
                <CardContent className="p-5 sm:p-6">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                                <KeyRound className="h-5 w-5" />
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-bold">Pedir código de acceso a los socios</p>
                                <p className="text-xs text-muted-foreground">
                                    {codeRequired
                                        ? 'Los socios deben ingresar el código diario en su auto check-in para ver sus recompensas.'
                                        : 'Los socios con membresía activa acceden directo a sus recompensas, sin ingresar código.'}
                                </p>
                            </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2 pt-1">
                            <span className="text-[10px] font-black uppercase tracking-[0.15em] text-muted-foreground">
                                {codeRequired ? 'Obligatorio' : 'Opcional'}
                            </span>
                            <Switch
                                checked={codeRequired}
                                onCheckedChange={handleToggleCodeRequired}
                                disabled={isSavingCodeSetting}
                                aria-label="Pedir código de acceso a los socios"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {codeRequired && <RewardAccessCodeCard gymId={gym.gym_id} />}

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Gift className="h-5 w-5 text-primary" />
                        Recompensas ({rewards.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 lg:p-6">
                    {isLoading ? (
                        <div className="py-12 flex justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : (
                        <>
                            <div className="hidden lg:block">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Título</TableHead>
                                            <TableHead>Tipo</TableHead>
                                            <TableHead>Vigencia</TableHead>
                                            <TableHead>Estado</TableHead>
                                            <TableHead className="text-right">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {rewards.map((reward) => (
                                            <TableRow key={reward.id}>
                                                <TableCell className="font-bold">{reward.title}</TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className="gap-1">
                                                        {reward.type === 'wheel' ? <RotateCw className="h-3 w-3" /> : <Flame className="h-3 w-3" />}
                                                        {reward.type === 'wheel' ? 'Ruleta' : 'Racha'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground">
                                                    {reward.starts_at ? new Date(reward.starts_at).toLocaleDateString('es-ES') : 'Siempre'}
                                                    {' — '}
                                                    {reward.ends_at ? new Date(reward.ends_at).toLocaleDateString('es-ES') : 'Sin fin'}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={reward.is_active ? 'default' : 'secondary'}>
                                                        {reward.is_active ? 'Activa' : 'Inactiva'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right space-x-1">
                                                    {reward.type === 'streak' && (() => {
                                                        const entriesCount = reward.entries?.length ?? 0;
                                                        const winner = reward.entries?.find(e => e.is_winner);
                                                        return (
                                                            <>
                                                                <span className="text-xs text-muted-foreground mr-1">
                                                                    {entriesCount} participantes
                                                                    {winner && ` · ${winner.client_name || 'Ganador'}`}
                                                                    {winner?.claimed_at && ' ✓'}
                                                                </span>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => openParticipants(reward)}
                                                                >
                                                                    <Medal className="h-4 w-4 mr-1" />
                                                                    Participantes
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    disabled={!!winner || entriesCount === 0}
                                                                    onClick={() => handleDraw(reward)}
                                                                >
                                                                    <Dices className="h-4 w-4 mr-1" />
                                                                    Sortear
                                                                </Button>
                                                                {winner && !winner.claimed_at && (
                                                                    <Button variant="gradient" size="sm" onClick={() => handleClaim(reward)}>
                                                                        <Trophy className="h-4 w-4 mr-1" />
                                                                        Reclamar
                                                                    </Button>
                                                                )}
                                                                {winner?.claimed_at && (
                                                                    <Badge variant="secondary" className="gap-1 align-middle">
                                                                        <CheckCircle2 className="h-3 w-3" /> Reclamado
                                                                    </Badge>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                    <Button variant="ghost" size="sm" onClick={() => handleToggleActive(reward)}>
                                                        {reward.is_active ? 'Desactivar' : 'Activar'}
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => openEdit(reward)}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(reward)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            <div className="lg:hidden divide-y divide-border">
                                {rewards.map((reward) => (
                                    <div key={reward.id} className="p-4 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <p className="font-bold">{reward.title}</p>
                                            <Badge variant={reward.is_active ? 'default' : 'secondary'}>
                                                {reward.is_active ? 'Activa' : 'Inactiva'}
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {reward.type === 'wheel' ? 'Ruleta de sorteo' : 'Racha de asistencia'}
                                        </p>
                                        {reward.type === 'streak' && (() => {
                                            const entriesCount = reward.entries?.length ?? 0;
                                            const winner = reward.entries?.find(e => e.is_winner);
                                            return (
                                                <div className="space-y-2">
                                                    <p className="text-xs text-muted-foreground">
                                                        {entriesCount} participantes
                                                        {winner && ` · Ganador: ${winner.client_name || 'sí'}`}
                                                        {winner?.claimed_at && ' (reclamado)'}
                                                    </p>
                                                    <div className="flex gap-2">
                                                        <Button variant="outline" size="sm" onClick={() => openParticipants(reward)}>
                                                            <Medal className="h-4 w-4 mr-1" /> Participantes
                                                        </Button>
                                                        <Button variant="outline" size="sm" disabled={!!winner || entriesCount === 0} onClick={() => handleDraw(reward)}>
                                                            <Dices className="h-4 w-4 mr-1" /> Sortear
                                                        </Button>
                                                        {winner && !winner.claimed_at && (
                                                            <Button variant="gradient" size="sm" onClick={() => handleClaim(reward)}>
                                                                <Trophy className="h-4 w-4 mr-1" /> Reclamar
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                        <div className="flex gap-1">
                                            <Button variant="outline" size="sm" onClick={() => handleToggleActive(reward)}>
                                                {reward.is_active ? 'Desactivar' : 'Activar'}
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => openEdit(reward)}><Pencil className="h-4 w-4" /></Button>
                                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(reward)}><Trash2 className="h-4 w-4" /></Button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {rewards.length === 0 && (
                                <div className="py-12 text-center text-muted-foreground">
                                    No hay recompensas creadas.
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingReward ? 'Editar Recompensa' : 'Nueva Recompensa'}</DialogTitle>
                        <DialogDescription>
                            Configura una oferta de ruleta o una racha de asistencia para tus socios.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                            <Label>Tipo de dinámica</Label>
                            <Select
                                value={form.type}
                                onValueChange={(v: RewardType) => setForm({ ...form, type: v })}
                                disabled={isSubmitting || !!editingReward}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="wheel">Ruleta de sorteo</SelectItem>
                                    <SelectItem value="streak">Racha de asistencia</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Título</Label>
                            <Input
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder="Ej: Ruleta de bienvenida"
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Descripción</Label>
                            <Textarea
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                placeholder="Describe el premio o la dinámica"
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Inicia</Label>
                                <Input
                                    type="date"
                                    value={form.starts_at}
                                    onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Termina</Label>
                                <Input
                                    type="date"
                                    value={form.ends_at}
                                    onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        {form.type === 'wheel' ? (
                            <div className="space-y-3">
                                <div className="space-y-2">
                                    <Label>Jugadas por socio</Label>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={form.plays_per_client}
                                        onChange={(e) => setForm({ ...form, plays_per_client: parseInt(e.target.value) || 1 })}
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Premios (segmentos de la ruleta)</Label>
                                    {prizes.map((prize, index) => (
                                        <div key={index} className="flex gap-2 items-center">
                                            <Input
                                                value={prize.label}
                                                onChange={(e) => updatePrize(index, { label: e.target.value })}
                                                placeholder="Premio"
                                                disabled={isSubmitting}
                                            />
                                            <Input
                                                type="number"
                                                min={1}
                                                className="w-24"
                                                value={prize.weight}
                                                onChange={(e) => updatePrize(index, { weight: parseInt(e.target.value) || 1 })}
                                                title="Peso / probabilidad"
                                                disabled={isSubmitting}
                                            />
                                            <Input
                                                type="number"
                                                className="w-24"
                                                value={prize.stock ?? ''}
                                                onChange={(e) => updatePrize(index, { stock: e.target.value === '' ? null : parseInt(e.target.value) })}
                                                placeholder="Stock"
                                                title="Stock (vacío = ilimitado)"
                                                disabled={isSubmitting}
                                            />
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="text-destructive shrink-0"
                                                onClick={() => setPrizes(prizes.filter((_, i) => i !== index))}
                                                disabled={isSubmitting || prizes.length <= 1}
                                            >
                                                <Trash className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPrizes([...prizes, { label: '', weight: 1, stock: null }])}
                                        disabled={isSubmitting}
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Agregar premio
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <p className="text-xs text-muted-foreground">
                                    Al alcanzar los días de asistencia, el socio entra a la rifa. El ganador se sortea luego desde la lista con "Sortear".
                                </p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Días requeridos</Label>
                                        <Input
                                            type="number"
                                            min={1}
                                            value={form.threshold}
                                            onChange={(e) => setForm({ ...form, threshold: parseInt(e.target.value) || 1 })}
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Ventana (días)</Label>
                                        <Input
                                            type="number"
                                            min={1}
                                            value={form.window_days}
                                            onChange={(e) => setForm({ ...form, window_days: parseInt(e.target.value) || 1 })}
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Premio de la rifa</Label>
                                    <Input
                                        value={streakPrize}
                                        onChange={(e) => setStreakPrize(e.target.value)}
                                        placeholder="Ej: 1 mes de gym gratis"
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Stock (vacío = ilimitado)</Label>
                                    <Input
                                        type="number"
                                        value={streakStock ?? ''}
                                        onChange={(e) => setStreakStock(e.target.value === '' ? null : parseInt(e.target.value))}
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSubmitting}>Cancelar</Button>
                        <Button variant="gradient" onClick={handleSave} disabled={isSubmitting}>
                            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Guardar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <RewardParticipantsSheet
                reward={participantsReward}
                open={isParticipantsOpen}
                onOpenChange={setIsParticipantsOpen}
            />
        </DashboardLayout>
    );
}
